"use client";

import { useEffect, useId, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { sendGTMEvent } from "@next/third-parties/google";

declare global {
  interface Window {
    hbspt?: {
      forms?: {
        create: (options: Record<string, unknown>) => void;
      };
    };
  }
}

type HubSpotFormProps = {
  portalId?: string;
  formId?: string;
  region?: string;
  deferUntilVisible?: boolean;
  rootMargin?: string;
};

let hubspotFormsScriptPromise: Promise<void> | null = null;

const loadHubSpotFormsScript = () => {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (window.hbspt?.forms?.create) {
    return Promise.resolve();
  }

  if (hubspotFormsScriptPromise) {
    return hubspotFormsScriptPromise;
  }

  const scriptSrc = "https://js.hsforms.net/forms/embed/v2.js";
  hubspotFormsScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`) as
      | HTMLScriptElement
      | null;

    if (existingScript) {
      if (window.hbspt?.forms?.create) {
        resolve();
        return;
      }
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("HubSpot script failed")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("HubSpot script failed"));
    document.body.appendChild(script);
  });

  return hubspotFormsScriptPromise;
};

export const HubSpotForm = ({
  portalId,
  formId,
  region = "na1",
  deferUntilVisible = true,
  rootMargin = "280px",
}: HubSpotFormProps) => {
  const t = useTranslations("hubspotForm");
  const [error, setError] = useState<string | null>(null);
  const rawFormContainerId = useId();
  const formContainerId = `hubspot-form-${rawFormContainerId.replace(/:/g, "")}`;
  const [shouldLoad, setShouldLoad] = useState(!deferUntilVisible);
  const formCreatedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!deferUntilVisible || shouldLoad) return;

    const target = containerRef.current;
    if (!target) return;

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- fallback branch of an IntersectionObserver subscription effect; needs containerRef to be attached post-mount
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [deferUntilVisible, rootMargin, shouldLoad]);

  useEffect(() => {
    formCreatedRef.current = false;
    const target = document.getElementById(formContainerId);
    if (target) target.innerHTML = "";
  }, [formContainerId, formId, portalId, region]);

  useEffect(() => {
    if (!portalId || !formId || !shouldLoad) return;
    let cancelled = false;

    const createForm = async () => {
      try {
        await loadHubSpotFormsScript();
        if (cancelled || formCreatedRef.current || !window.hbspt?.forms?.create) return;

        const target = document.getElementById(formContainerId);
        if (!target) return;

        target.innerHTML = "";
        window.hbspt.forms.create({
          region,
          portalId,
          formId,
          target: `#${formContainerId}`,
        });
        formCreatedRef.current = true;
      } catch {
        if (!cancelled) {
          setError(t("loadError"));
        }
      }
    };

    createForm();
    return () => {
      cancelled = true;
    };
  }, [formContainerId, formId, portalId, region, shouldLoad, t]);

  useEffect(() => {
    if (!portalId || !formId) return;

    const handleHubSpotMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; eventName?: string; id?: string } | undefined;
      if (data?.type !== "hsFormCallback" || data.eventName !== "onFormSubmitted") return;
      if (data.id && data.id !== formId) return;

      sendGTMEvent({ event: "generate_lead", form_id: formId, form_type: "hubspot" });
    };

    window.addEventListener("message", handleHubSpotMessage);
    return () => window.removeEventListener("message", handleHubSpotMessage);
  }, [formId, portalId]);

  if (!portalId || !formId) {
    return (
    <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-gray-600 break-words dark:border-gray-700 dark:bg-surface-dark-raised dark:text-gray-300">
        {t("missingConfigBefore")}
        <code className="mx-1 inline-flex max-w-full break-all rounded bg-gray-800 px-2 py-1 text-xs font-medium text-white sm:text-sm">
          NEXT_PUBLIC_HUBSPOT_PORTAL_ID
        </code>
        {t("missingConfigAnd")}
        <code className="mx-1 inline-flex max-w-full break-all rounded bg-gray-800 px-2 py-1 text-xs font-medium text-white sm:text-sm">
          NEXT_PUBLIC_HUBSPOT_FORM_ID
        </code>
        {t("missingConfigAfter")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-400/40 dark:bg-red-900/20 dark:text-red-200">
        {error}
      </div>
    );
  }

  return (
    <div
      id={formContainerId}
      ref={containerRef}
      className="hubspot-light min-h-[340px]"
      onClick={() => setShouldLoad(true)}
      onMouseEnter={() => setShouldLoad(true)}
    />
  );
};
